import { httpClient } from '@/core/api/http-client'
import type {
  AddonGroup,
  AddonGroupDetail,
  AddonGroupInput,
} from '@/domain/entities/addon-group.entity'
import type { Paged, PageParams } from '@/domain/entities/paging'
import type { AddonGroupRepository } from '@/domain/repositories/addon-group.repository'
import { unwrap, unwrapList, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { AddonGroupDetailDto, AddonGroupDto, ProductIdsDto } from './addon-group.dto'
import { addonGroupMapper } from './addon-group.mapper'

export class AddonGroupHttpRepository implements AddonGroupRepository {
  async list(outletId: string, params: PageParams): Promise<Paged<AddonGroup>> {
    const raw = await httpClient.get<unknown>('/addon-groups', {
      params: {
        outlet_id: outletId,
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
      },
    })
    const paged = unwrapPaged<AddonGroupDto>(raw, params.page, params.limit)
    return { ...paged, items: paged.items.map(addonGroupMapper.toAddonGroup) }
  }

  async listAll(outletId: string): Promise<AddonGroup[]> {
    const raw = await httpClient.get<unknown>('/addon-groups', {
      params: { outlet_id: outletId, limit: 100 },
    })
    return unwrapList<AddonGroupDto>(raw).map(addonGroupMapper.toAddonGroup)
  }

  async get(id: string): Promise<AddonGroupDetail> {
    const raw = await httpClient.get<unknown>(`/addon-groups/${id}`)
    return addonGroupMapper.toAddonGroupDetail(unwrap<AddonGroupDetailDto>(raw))
  }

  async create(outletId: string, input: AddonGroupInput): Promise<AddonGroup> {
    const raw = await httpClient.post<unknown>('/addon-groups', {
      outlet_id: outletId,
      name: input.name,
      sku: input.sku ?? '',
      // WAJIB dikirim meski kosong: BE menolak 400 kalau field-nya absen,
      // supaya tidak diam-diam diartikan "kosongkan". Isi grup diatur setelah
      // grupnya ada, lewat PUT /addon-groups/:id/addons.
      addon_ids: [],
    })
    return addonGroupMapper.toAddonGroup(unwrap<AddonGroupDto>(raw))
  }

  async update(id: string, input: AddonGroupInput): Promise<AddonGroup> {
    // Partial update: addon_ids sengaja TIDAK dikirim di sini agar isi grup
    // tidak ikut tersapu saat user cuma mengganti nama.
    const raw = await httpClient.put<unknown>(`/addon-groups/${id}`, {
      name: input.name,
      sku: input.sku ?? '',
    })
    return addonGroupMapper.toAddonGroup(unwrap<AddonGroupDto>(raw))
  }

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/addon-groups/${id}`)
  }

  async setAddons(id: string, addonIds: string[]): Promise<AddonGroup> {
    const raw = await httpClient.put<unknown>(`/addon-groups/${id}/addons`, {
      addon_ids: addonIds,
    })
    return addonGroupMapper.toAddonGroup(unwrap<AddonGroupDto>(raw))
  }

  async setProducts(id: string, productIds: string[]): Promise<string[]> {
    const raw = await httpClient.put<unknown>(`/addon-groups/${id}/products`, {
      product_ids: productIds,
    })
    return unwrap<ProductIdsDto>(raw).product_ids ?? []
  }
}
